import type {
  AccountPackagesSummary, ApiResponse, OrganizerAddonRow, OrganizerOrderRow, PendingPackage, WeddingPackageInfo,
} from "@wedding/shared";
import type { QrAnalytics } from "@wedding/qr-analytics";

/**
 * Organizer API client — always uses relative paths.
 * In production, organizer.wedding.mertbal.com proxies /api/* to the Worker.
 * In dev, Vite proxy forwards /api/* to localhost:8787.
 */

async function request<T>(
  base: string,
  path: string,
  options?: RequestInit,
): Promise<ApiResponse<T>> {
  const url = path && path !== "/" ? `${base}${path}` : base;
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options?.headers },
    ...options,
  });
  const text = await res.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new Error(`Sunucu hatası: ${res.status} ${res.statusText}`);
  }
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("organizer:session-expired"));
    throw new Error(json.error ?? "Session expired");
  }
  if (!res.ok) throw new Error(json.error ?? `Request failed: ${res.status}`);
  return json;
}

export interface ActivityItem {
  type: "upload" | "upload_pending" | "like" | "comment";
  id: string;
  guest_id: string;
  guest_name: string | null;
  media_id: string;
  media_r2_key: string;
  media_kind: "image" | "video";
  content: string | null;
  created_at: string;
}

export interface AuditLogEntry {
  id: string;
  actor_type: "admin" | "organizer" | "guest" | "system";
  actor_identity: string | null;
  action: string;
  resource_type: string;
  created_at: string;
}

/** Per-wedding organizer API */
export const createOrganizerApi = (slug: string) => {
  const base = `/api/organizer/w/${slug}`;
  return {
    get: <T>(path: string) => request<T>(base, path),
    post: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(base, path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string) =>
      request<T>(base, path, { method: "DELETE" }),
    getAuditLog: (limit = 10) =>
      request<AuditLogEntry[]>(base, `/audit-log?limit=${limit}`),
    getActivity: (limit = 10) =>
      request<ActivityItem[]>(base, `/activity?filter=all&limit=${limit}`),
  };
};

/** Organizer auth API (OTP login) */
export const organizerAuthApi = (() => {
  const base = `/api/organizer/auth`;
  return {
    requestCode: (email: string, turnstileToken: string) =>
      request<{ sent: boolean }>(base, "/request-code", {
        method: "POST",
        body: JSON.stringify({ email, turnstileToken }),
      }),
    verifyCode: (email: string, code: string) =>
      request<{
        organizer: { id: string; email: string; displayName: string | null; plan: string };
      }>(base, "/verify-code", {
        method: "POST",
        body: JSON.stringify({ email, code }),
      }),
    me: () =>
      request<{
        organizer: { id: string; email: string; displayName: string | null; plan: string };
      }>(base, "/me"),
    /** Admin yeni hesap açmayı açtı mı? (giriş ekranındaki "Hesap oluştur" / "Teklif al") */
    signupStatus: () => request<{ open: boolean }>(base, "/signup-status"),
    /** Kayıt: hesap kod doğrulanınca açılır (`verifyCode`). Yanıt her durumda nötr. */
    signup: (data: { name: string; email: string; acceptTerms: true; turnstileToken: string }) =>
      request<{ sent: boolean }>(base, "/signup", { method: "POST", body: JSON.stringify(data) }),
    logout: () => request(base, "/logout", { method: "POST" }),
  };
})();

/** Organizer profile API */
export const organizerProfileApi = {
  get: () =>
    request<{
      organizer: Record<string, unknown>;
      usage: { weddings: number };
      /** DB'den plan adı + düğün limiti (özel planlar dahil). Eski worker'da yok. */
      planInfo?: { label: string; maxWeddings: number; accountWide?: boolean };
    }>(
      "/api/organizer/profile",
      "/",
    ),
  update: (data: { displayName?: string; phoneE164?: string }) =>
    request("/api/organizer/profile", "/", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deactivate: () =>
    request("/api/organizer/profile", "/", { method: "DELETE" }),
  requestPhoneVerify: () =>
    request<{ verificationSid: string | null }>("/api/organizer/profile", "/phone/request-verify", { method: "POST" }),
  verifyPhone: (body: { code: string; verificationSid?: string }) =>
    request("/api/organizer/profile", "/phone/verify", { method: "POST", body: JSON.stringify(body) }),
};

/** Organizer wedding list API */
export const organizerWeddingsApi = {
  list: () =>
    request<{
      weddings: Array<{
        slug: string;
        title: string;
        date_time: string | null;
        location: string | null;
        /** Düğünün paketi (sipariş ya da hesap planı) — eski worker'da yok. */
        package?: WeddingPackageInfo;
      }>;
      archivedWeddings: Array<{
        slug: string;
        title: string;
        date_time: string | null;
        location: string | null;
      }>;
      total: number;
      /** Ücretsiz deneme (paketsiz) düğün hakkı — hesap planından. */
      maxWeddings: number;
      /** Paketsiz düğün sayısı (ücretli düğünler deneme hakkını tüketmez). Eski worker'da yok. */
      freeUsed?: number;
      /** DB'den plan adı (özel planlar dahil). Eski worker'da yok. */
      planLabel?: string;
      /** Paketsiz düğünün misafir limiti (hesap planından). Eski worker'da yok. */
      planGuests?: number;
      /** Hesaba özel plan (eski Pro/Max ya da kurumsal) — varsayılan deneme planında null. */
      accountWide?: { slug: string; label: string } | null;
      /** Ödenmiş, düğüne tanımlanmayı bekleyen paketler — organizatör seçer. */
      pendingPackages?: PendingPackage[];
      /** Paket modeli (2026-10-16): kullanılabilir / kullanımda / ödeme bekleyen / biten paketler. Eski worker'da yok. */
      packages?: AccountPackagesSummary;
    }>("/api/organizer/weddings", "/"),
  create: (data: {
    slug: string;
    title: string;
    dateTime?: string;
    location?: string;
    description?: string;
    /** Bu düğünün paketi: Free (`fr-…`), ödenmiş sipariş id'si ya da (058 öncesi) "trial". Zorunlu. */
    packageId?: string;
    /** Ücretli pakette zorunlu: hizmet hemen başlar, cayma hakkı biter (m.15). */
    startConsent?: boolean;
  }) =>
    request<{ id: string; slug: string; packageOrder?: string | null }>("/api/organizer/weddings", "/", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  archive: (slug: string) =>
    request("/api/organizer/weddings", `/${slug}/archive`, { method: "PATCH" }),
  unarchive: (slug: string) =>
    request("/api/organizer/weddings", `/${slug}/unarchive`, { method: "PATCH" }),
  checkSlug: (slug: string) =>
    request<{ available: boolean }>("/api/organizer/weddings", `/check-slug?slug=${encodeURIComponent(slug)}`),
};

/** "Siparişlerim" + bekleyen paketi düğüne tanımla (worker routes/organizer-orders.ts). */
export const organizerOrdersApi = {
  list: () => request<{ orders: OrganizerOrderRow[]; pendingPackages: PendingPackage[] }>("/api/organizer/orders", ""),
  apply: (orderId: string, slug: string, startConsent = false) =>
    request<{ number: string; wedding: { slug: string; title: string } }>("/api/organizer/orders", `/${orderId}/apply`, {
      method: "POST",
      body: JSON.stringify({ slug, startConsent }),
    }),
};

/** Paketlerim: paketler + tam sipariş geçmişi + ekstra kişi paketleri; paketi düğüne tanımla (worker routes/organizer-packages.ts). */
export const organizerPackagesApi = {
  list: () =>
    request<{ packages: AccountPackagesSummary; orders: OrganizerOrderRow[]; addons: OrganizerAddonRow[] }>("/api/organizer/packages", ""),
  assign: (packageId: string, slug: string, startConsent: boolean) =>
    request<{ number: string | null; wedding: { slug: string; title: string } }>("/api/organizer/packages", `/${packageId}/assign`, {
      method: "POST",
      body: JSON.stringify({ slug, startConsent }),
    }),
};

/** Organizer per-wedding background images API */
export const createOrganizerBgApi = (slug: string) => ({
  upload: async (slot: string, file: File): Promise<{ url: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/organizer/weddings/${slug}/backgrounds/${slot}`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    const json = await res.json() as { ok: boolean; data?: { url: string }; error?: string };
    if (!json.ok) throw new Error(json.error ?? "Upload failed");
    return json.data!;
  },
  delete: (slot: string) =>
    request<void>(`/api/organizer/weddings/${slug}`, `/backgrounds/${slot}`, { method: "DELETE" }),
});

interface SupportMessage {
  id: string;
  author_type: "organizer" | "admin";
  content: string;
  attachment_keys: string | null; // JSON string: ["key1", "key2"] or null
  created_at: string;
}

/** Organizer support tickets API */
export const organizerSupportApi = {
  list: (cursor?: string) => {
    const params = new URLSearchParams({ limit: "20" });
    if (cursor) params.set("cursor", cursor);
    return request<Array<{
      id: string; ticket_number: number; subject: string; status: string;
      priority: string; category: string; organizer_unread: number;
      wedding_slug: string | null; created_at: string; updated_at: string;
    }>>("/api/organizer/support", `?${params}`);
  },
  create: (data: {
    subject: string; body: string; weddingSlug?: string;
    priority?: string; category?: string;
  }) => request<{ id: string }>("/api/organizer/support", "/", {
    method: "POST", body: JSON.stringify(data),
  }),
  get: (id: string) => request<{
    id: string; ticket_number: number; subject: string; body: string;
    status: string; priority: string; category: string;
    organizer_unread: number; csat_score: number | null; csat_token: string | null;
    wedding_slug: string | null; created_at: string; updated_at: string;
    messages: SupportMessage[];
  }>("/api/organizer/support", `/${id}`),
  sendMessage: (id: string, content: string, attachmentKeys?: string[]) =>
    request<{ id: string; created_at: string }>("/api/organizer/support", `/${id}/messages`, {
      method: "POST",
      body: JSON.stringify({ content, attachmentKeys: attachmentKeys ?? [] }),
    }),
  markRead: (id: string) =>
    request<void>("/api/organizer/support", `/${id}/read`, { method: "POST" }),
  reopen: (id: string) =>
    request<void>("/api/organizer/support", `/${id}/reopen`, { method: "POST" }),
  rateCsat: (id: string, score: number) =>
    request<void>("/api/organizer/support", `/${id}/csat`, {
      method: "POST", body: JSON.stringify({ score }),
    }),
  uploadAttachments: async (ticketId: string, files: File[]): Promise<string[]> => {
    const fd = new FormData();
    files.forEach((f) => fd.append("file", f));
    const res = await fetch(`/api/organizer/support/${ticketId}/attachments`, {
      method: "POST", credentials: "include", body: fd,
    });
    const json = await res.json() as { ok: boolean; data?: { keys: string[] }; error?: string };
    if (!json.ok) throw new Error(json.error ?? "Upload failed");
    return json.data?.keys ?? [];
  },
  attachmentUrl: (ticketId: string, key: string) =>
    `/api/organizer/support/${ticketId}/attachments/${key}`,
};

export interface AlbumSummary {
  id: string;
  kind: "favorites" | "custom";
  name: string;
  cover_media_id: string | null;
  cover_r2_key: string | null;
  position: number;
  is_public: number;
  created_at: string;
  item_count: number;
}

export interface AlbumItem {
  media_id: string;
  r2_key: string;
  kind: "image" | "video";
  content_type: string;
  created_at: string;
  position: number;
  added_at: string;
}

export interface GalleryMedia {
  id: string;
  kind: "image" | "video";
  r2_key: string;
  content_type: string;
  bytes: number;
  created_at: string;
  guest_phone: string | null;
  guest_name: string | null;
  album_ids: string[];
  is_favorite: boolean;
  like_count: number;
  comment_count: number;
}

export interface MediaComment {
  id: string;
  content: string;
  author_name: string;
  guest_id: string;
  created_at: string;
  is_hidden: boolean;
}

export function createOrganizerMediaSocialApi(slug: string) {
  const base = `/api/organizer/w/${slug}`;
  return {
    getComments: async (mediaId: string, cursor?: string): Promise<{ comments: MediaComment[]; hasMore: boolean; like_count: number; comment_count: number }> => {
      const url = cursor
        ? `${base}/media/${mediaId}/comments?cursor=${encodeURIComponent(cursor)}`
        : `${base}/media/${mediaId}/comments`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to load comments: ${res.status}`);
      const json = await res.json() as { ok: boolean; data: MediaComment[]; hasMore: boolean; like_count: number; comment_count: number };
      return {
        comments: (json.data ?? []).map(c => ({ ...c, is_hidden: Boolean(c.is_hidden) })),
        hasMore: json.hasMore ?? false,
        like_count: json.like_count ?? 0,
        comment_count: json.comment_count ?? 0,
      };
    },
    deleteComment: async (mediaId: string, commentId: string): Promise<void> => {
      const res = await fetch(`${base}/media/${mediaId}/comments/${commentId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    },
    toggleCommentVisibility: async (mediaId: string, commentId: string, isHidden: boolean): Promise<void> => {
      const res = await fetch(`${base}/media/${mediaId}/comments/${commentId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_hidden: isHidden }),
      });
      if (!res.ok) throw new Error(`Toggle visibility failed: ${res.status}`);
    },
  };
}

export const createAlbumApi = (slug: string) => {
  const base = `/api/organizer/w/${slug}`;
  return {
    listAlbums: () =>
      request<AlbumSummary[]>(base, "/albums"),
    createAlbum: (name: string) =>
      request<{ id: string }>(base, "/albums", { method: "POST", body: JSON.stringify({ name }) }),
    renameAlbum: (id: string, name: string) =>
      request<void>(base, `/albums/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    deleteAlbum: (id: string) =>
      request<void>(base, `/albums/${id}`, { method: "DELETE" }),
    getAlbumItems: (id: string, cursor?: string, order?: "asc" | "desc"): Promise<{ ok: boolean; data: AlbumItem[]; hasMore: boolean; error?: string }> => {
      const qs = new URLSearchParams();
      if (cursor) qs.set("cursor", cursor);
      if (order) qs.set("order", order);
      const q = qs.toString();
      return request<AlbumItem[]>(base, `/albums/${id}/items${q ? `?${q}` : ""}`) as Promise<{ ok: boolean; data: AlbumItem[]; hasMore: boolean; error?: string }>;
    },
    addItem: (albumId: string, mediaId: string) =>
      request<void>(base, `/albums/${albumId}/items`, { method: "POST", body: JSON.stringify({ media_id: mediaId }) }),
    removeItem: (albumId: string, mediaId: string) =>
      request<void>(base, `/albums/${albumId}/items/${mediaId}`, { method: "DELETE" }),
    setCover: (albumId: string, mediaId: string | null) =>
      request<void>(base, `/albums/${albumId}/cover`, { method: "PATCH", body: JSON.stringify({ media_id: mediaId }) }),
    setPublic: (id: string, isPublic: boolean) =>
      request<void>(base, `/albums/${id}`, { method: "PATCH", body: JSON.stringify({ is_public: isPublic }) }),
    toggleFavorite: (mediaId: string) =>
      request<{ favorited: boolean }>(base, `/media/${mediaId}/favorite`, { method: "POST" }),
    getGallery: (cursor?: string, opts?: { kind?: "image" | "video"; search?: string }) => {
      const qs = new URLSearchParams();
      if (cursor) qs.set("cursor", cursor);
      if (opts?.kind) qs.set("kind", opts.kind);
      if (opts?.search) qs.set("search", opts.search);
      const q = qs.toString();
      return request<{ media: GalleryMedia[]; hasMore: boolean }>(base, `/media/gallery${q ? `?${q}` : ""}`);
    },
  };
};

/** Payload shape is owned by the shared page package (single source of truth). */
export type { QrAnalytics } from "@wedding/qr-analytics";

/**
 * Per-wedding QR scan analytics API — satisfies the shared `QrAnalyticsApi`
 * contract consumed by `@wedding/qr-analytics`.
 */
export const createAnalyticsApi = (slug: string) => {
  const base = `/api/organizer/w/${slug}`;
  return {
    getQr: () => request<QrAnalytics>(base, "/analytics/qr"),
    /** Fetch the CSV export as a blob (sends the organizer cookie). `lang`
     *  localizes the CSV section headers to the organizer's UI language. */
    exportCsv: async (lang?: string): Promise<Blob> => {
      const qs = lang ? `?lang=${encodeURIComponent(lang)}` : "";
      const res = await fetch(`${base}/analytics/qr/export.csv${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      return res.blob();
    },
  };
};

/** Join page theme API */
export const createJoinThemeApi = (slug: string) => {
  const base = `/api/organizer/w/${slug}/join-theme`;

  return {
    updateConfig: (body: {
      layoutId?: string;
      config?: Record<string, unknown>;
      preset?: string;
      palette?: string;
      fontPair?: string;
    }) =>
      request<void>(base, "/config/join", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),

    /** Upload a layout-specific asset (e.g. Onyx LOOK card image). */
    uploadAsset: async (
      file: File,
      opts: { layoutId: string; slot: string },
    ): Promise<{ url: string; key: string }> => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("layoutId", opts.layoutId);
      fd.append("slot", opts.slot);
      const res = await fetch(`${base}/join-asset`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const json = (await res.json()) as { ok: boolean; data?: { url: string; key: string }; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Upload failed");
      return json.data!;
    },

    /** Delete a layout-specific asset by R2 key. */
    deleteAsset: (key: string) =>
      request<void>(base, "/join-asset", {
        method: "DELETE",
        body: JSON.stringify({ key }),
      }),

    uploadHero: async (file: File): Promise<{ url: string }> => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${base}/join-hero`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const json = await res.json() as { ok: boolean; data?: { url: string }; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Upload failed");
      return json.data!;
    },

    deleteHero: () =>
      request<void>(base, "/join-hero", { method: "DELETE" }),

    addGalleryImage: async (file: File): Promise<{ url: string; index: number }> => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${base}/join-images`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const json = await res.json() as { ok: boolean; data?: { url: string; index: number }; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Upload failed");
      return json.data!;
    },

    deleteGalleryImage: (index: number) =>
      request<void>(base, `/join-images/${index}`, { method: "DELETE" }),

    uploadFrame: async (blob: Blob, index: number): Promise<void> => {
      const fd = new FormData();
      fd.append("file", blob, `frame-${index}.jpg`);
      const res = await fetch(`${base}/join-frames/${index}`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const json = await res.json() as { ok: boolean; error?: string };
      if (!json.ok) throw new Error(json.error ?? "Frame upload failed");
    },

    setFrameCount: (count: number) =>
      request<void>(base, "/join-frame-count", {
        method: "PATCH",
        body: JSON.stringify({ count }),
      }),

    deleteFrames: () =>
      request<void>(base, "/join-frames", { method: "DELETE" }),
  };
};
